/**
 * UI-POLISH-3 — the login identifier's placeholder, rotating between the two things you can type.
 *
 * WHY A COMPONENT RATHER THAN A `placeholder` ATTRIBUTE. A native placeholder cannot animate; it is
 * a string the browser paints. So this is an overlay positioned where the placeholder would sit,
 * shown only while the field is genuinely empty and unfocused, with the real input's `placeholder`
 * left blank so the two can never both appear.
 *
 * IT STOPS THE MOMENT IT WOULD BE A NUISANCE. Focus the field, or type a single character, and the
 * rotation ends and the overlay disappears — nothing moving under a cursor while someone is trying
 * to enter a phone number.
 *
 * REDUCED MOTION IS NOT A LESSER VERSION. `prefers-reduced-motion` gets one static line naming both
 * options, so the information is identical and only the movement is gone. Someone who asked their
 * device for less motion should not have to infer that a second option exists.
 */
import { useEffect, useState } from 'react';

interface Props {
  /** The phrases to cycle. Two is the intended shape; more works. */
  options: string[];
  /** Static text for reduced-motion / paused states — should name every option. */
  staticText: string;
  /** Rotation is suppressed while true (focused, or the field has a value). */
  paused: boolean;
  intervalMs?: number;
}

/** Matched to the CSS transition below — the outgoing line must finish before the next swap. */
const SLIDE_MS = 320;

export function RotatingPlaceholder({ options, staticText, paused, intervalMs = 2600 }: Props) {
  const reduceMotion =
    typeof window !== 'undefined' &&
    !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  const [index, setIndex] = useState(0);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (paused || reduceMotion || options.length < 2) return;
    const tick = window.setInterval(() => {
      // Two-phase: slide the current line out, then swap the text and let the new one slide in.
      setLeaving(true);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % options.length);
        setLeaving(false);
      }, SLIDE_MS);
    }, intervalMs);
    return () => window.clearInterval(tick);
  }, [paused, reduceMotion, options.length, intervalMs]);

  if (paused) return null;

  /*
   * [OCCASION-CARDS-1 Slice 2] POSITIONING IS NO LONGER THIS COMPONENT'S JOB.
   *
   * It used to place itself with `position:absolute; left:44; top:50%` against whatever wrapper it
   * happened to be dropped into. On the login page that wrapper contained AuthInput, which renders a
   * LABEL above the field — so "50%" was the centre of label + gap + input, and the hint floated
   * visibly above the line the typed text would appear on. The left offset was a hardcoded 44 that
   * merely happened to be near the input's real 42.
   *
   * Now it is a plain inline element handed to AuthInput's `overlay` slot, which places it inside the
   * input's own box at the input's own text offset. The two can no longer disagree.
   *
   * THE CLIP BOX IS SIZED IN `em`, NOT PIXELS. A fixed `height: 20` was a guess about the line box at
   * 14px; at any other size the text would sit off-centre inside its own clipper. `1.4em` tracks the
   * font, and `lineHeight: 1.4` on the moving span makes the resting position identical to the
   * clipper's centre — so the text sits on the same baseline whether it is mid-slide or at rest,
   * which is the second half of what "centred in both animation states" requires.
   */
  return (
    <span
      style={{
        display: 'block',
        overflow: 'hidden',
        height: '1.4em',
        lineHeight: 1.4,
        color: 'var(--t4)',
        fontSize: 14,
        fontWeight: 500, // matches the input's own text weight, so the hint reads as the same field
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {reduceMotion ? (
        staticText
      ) : (
        <span
          style={{
            display: 'block',
            lineHeight: 1.4,
            transition: `transform ${SLIDE_MS}ms ease, opacity ${SLIDE_MS}ms ease`,
            transform: leaving ? 'translateY(-100%)' : 'translateY(0)',
            opacity: leaving ? 0 : 1,
          }}
        >
          {options[index]}
        </span>
      )}
    </span>
  );
}

export default RotatingPlaceholder;
